import { env } from '../../env';
import { CustomerSettingsData, LoginSettings, RequestOptions, Settings, ToastTypeEnum } from '../../models/Index';
import { PatchLoginSettingsType } from '../../models/PatchLoginSettingsType';
import HttpService from '../httpService/HttpService';
import StateService from '../stateService/StateService';

const httpClient = HttpService.getAxiosClient();

// Basic stuff
const UD_BACKEND_URL = `${ env.REACT_APP_BACKEND_URL }/v1`;

function getLoginSettings( customerUuid: string, login: string, options?: RequestOptions ): Promise<LoginSettings> {
    return httpClient
        .get( `${ UD_BACKEND_URL }/login-settings/${ login }`, { ...options, headers: { customerUuid } } )
        .then( ( response ) => response.data )
        .catch( ( err ) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_LOGIN_SETTINGS_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next( { source: 'SETTINGS_SERVICE.GET_LOGIN_SETTINGS', text: msg, type: ToastTypeEnum.ERROR } );
        } );
}

function patchLoginSettings(
    body: LoginSettings,
    customerUuid: string,
    login: string,
    options?: RequestOptions,
): Promise<PatchLoginSettingsType> {
    return httpClient
        .patch( `${ UD_BACKEND_URL }/logins/${ login }`, body, { ...options, headers: { customerUuid } } )
        .then( ( response ) => {
            StateService.toastMessage$.next( {
                source: 'SETTINGS_SERVICE.PATCH_LOGIN_SETTINGS',
                text: 'TOAST_MESSAGE.LOGIN_SETTINGS_SUCCESSFULLY_SAVED_TEXT',
                type: ToastTypeEnum.SUCCESS,
            } );
            return response as PatchLoginSettingsType;
        } )
        .catch( ( err ) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_SAVING_LOGIN_SETTINGS_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next( { source: 'SETTINGS_SERVICE.PATCH_LOGIN_SETTINGS', text: msg, type: ToastTypeEnum.ERROR } );
            return { data: undefined, status: 0 } as PatchLoginSettingsType; // Provide a default value for status
        } );
}

function getCustomerSettings( customerUuid: string, options?: RequestOptions ): Promise<CustomerSettingsData> {
    return httpClient
        .get( `${ UD_BACKEND_URL }/customer-settings`, { ...options, headers: { customerUuid } } )
        .then( ( response ) => {
            return response.data;
        } )
        .catch( ( err ) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_CUSTOMER_SETTINGS_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next( { source: 'SETTINGS_SERVICE.GET_CUSTOMER_SETTINGS', text: msg, type: ToastTypeEnum.ERROR } );
        } );
}

function postCustomerSettings( body: CustomerSettingsData, customerUuid: string, options?: RequestOptions ): Promise<any> {
    return httpClient
        .post( `${ UD_BACKEND_URL }/customer-settings`, body, { ...options, headers: { customerUuid } } )
        .then( ( response ) => {
            StateService.toastMessage$.next( {
                source: 'SETTINGS_SERVICE.POST_CUSTOMER_SETTINGS',
                text: 'TOAST_MESSAGE.CUSTOMER_SETTINGS_SUCCESSFULLY_SAVED_TEXT',
                type: ToastTypeEnum.SUCCESS,
            } );
            return response;
        } )
        .catch( ( err ) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_SAVING_CUSTOMER_SETTINGS_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next( { source: 'SETTINGS_SERVICE.POST_CUSTOMER_SETTINGS', text: msg, type: ToastTypeEnum.ERROR } );
        } );
}

function getDefaultCustomerSettings(): CustomerSettingsData {
    return {
        toastStyles: {
            error: {
                style: {
                    backgroundColor: '#D91111',
                },
                autoClose: 5000,
                pauseOnFocusLoss: true,
                position: 'top-center',
                hideProgressBar: false,
                closeManually: false
            },
            info: {
                autoClose: 5000,
                pauseOnFocusLoss: true,
                position: 'top-center',
                hideProgressBar: false,
                closeManually: false
            },
            success: {
                style: {
                    backgroundColor: '#49BA4E',
                },
                autoClose: 3000,
                pauseOnFocusLoss: true,
                position: 'top-center',
                hideProgressBar: false,
                closeManually: false
            },
            warn: {
                style: {
                    backgroundColor: '#F57316',
                },
                autoClose: 5000,
                pauseOnFocusLoss: true,
                position: 'top-center',
                hideProgressBar: false,
                closeManually: false
            },
        },
    } as Settings;
}

function getDefaultLoginSettings(): LoginSettings {
    return {
        language: 'de',
        duration: 300000,
    } as LoginSettings;
}

const SettingsService = {
    getCustomerSettings,
    getDefaultCustomerSettings,
    getDefaultLoginSettings,
    getLoginSettings,
    httpClient,
    patchLoginSettings,
    postCustomerSettings,
};

export default SettingsService;
